import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Pagination from "../../components/Pagination";
import "bootstrap-icons/font/bootstrap-icons.css";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const safeParse = (str, fallback = []) => {
  try {
    if (!str) return fallback;
    if (typeof str === "object") return Array.isArray(str) ? str : fallback;
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export default function InterviewManager() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [currentPageQuestions, setCurrentPageQuestions] = useState(1);
  const [currentPageDefault, setCurrentPageDefault] = useState(1);
  const [programPageMap, setProgramPageMap] = useState({});
  const itemsPerPage = 10;

  const [searchQuery, setSearchQuery] = useState("");

  const [allQuestions, setAllQuestions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);

  // Question modal state
  const [showQModal, setShowQModal] = useState(false);
  const [editingQ, setEditingQ] = useState(null);

  // Program modal state
  const [showProgModal, setShowProgModal] = useState(false);
  const [progName, setProgName] = useState("");
  const [progCats, setProgCats] = useState([]);

  const defaultProgram = useMemo(
    () =>
      programs.find(
        (p) => p.ip_key === "default" || p.ip_category_ids === null
      ),
    [programs]
  );
  const categoryPrograms = useMemo(
    () => programs.filter((p) => p.ip_category_ids !== null),
    [programs]
  );

  const getPaginatedData = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const loadAll = async () => {
    try {
      setErr("");
      setLoading(true);
      const [qRes, pRes, catRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/interviews/questions`, {
          headers: authHeader(),
        }),
        axios.get(`${BASE_URL}/api/interviews/programs`, {
          headers: authHeader(),
        }),
        axios.get(`${BASE_URL}/api/dropdowns/categories`, {
          headers: authHeader(),
        }),
      ]);
      setAllQuestions(qRes.data || []);
      setPrograms(pRes.data || []);
      setCategories(
        (catRes.data || []).map((c) => ({ label: c.c_name, value: c.c_id }))
      );
    } catch (e) {
      console.error(e);
      setErr("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setCurrentPageQuestions(1);
    setCurrentPageDefault(1);
    setProgramPageMap({});
  }, [allQuestions, programs]);

  // ---- QUESTIONS ----
  const openNewQ = () => {
    setEditingQ(null);
    setShowQModal(true);
  };

  const openEditQ = (q) => {
    setEditingQ(q);
    setShowQModal(true);
  };

  const saveQuestion = async (qObj) => {
    try {
      if (editingQ) {
        await axios.put(
          `${BASE_URL}/api/interviews/questions/${editingQ.iq_id}`,
          qObj,
          {
            headers: authHeader(),
          }
        );
      } else {
        await axios.post(`${BASE_URL}/api/interviews/questions`, qObj, {
          headers: authHeader(),
        });
      }
      setShowQModal(false);
      await loadAll();
    } catch (e) {
      alert("Failed to save question.");
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await axios.delete(`${BASE_URL}/api/interviews/questions/${id}`, {
      headers: authHeader(),
    });
    await loadAll();
  };

  // ---- PROGRAMS ----
  const addQtoProgram = (program, qid) => {
    const ids = safeParse(program.ip_question_ids, []);
    if (!ids.includes(qid)) ids.push(qid);
    program.ip_question_ids = JSON.stringify(ids);
    setPrograms([...programs]); // local update
  };

  const deleteQfromProgram = (program, qid) => {
    const ids = safeParse(program.ip_question_ids, []).filter(
      (id) => id !== qid
    );
    program.ip_question_ids = JSON.stringify(ids);
    setPrograms([...programs]);
  };

  const saveProgram = async (programLike) => {
    const payload = {
      name: programLike.ip_name,
      key: programLike.ip_key,
      questionIds: safeParse(programLike.ip_question_ids, []),
      categoryIds: safeParse(programLike.ip_category_ids, null),
      active: programLike.ip_active,
      order: programLike.ip_order,
    };
    await axios.put(
      `${BASE_URL}/api/interviews/programs/${programLike.ip_id}`,
      payload,
      {
        headers: authHeader(),
      }
    );
    await loadAll();
  };

  const createProgram = async () => {
    await axios.post(
      `${BASE_URL}/api/interviews/programs`,
      {
        name: progName,
        key: null,
        questionIds: [],
        categoryIds: progCats.map((c) => c.value),
      },
      { headers: authHeader() }
    );
    setShowProgModal(false);
    setProgName("");
    setProgCats([]);
    await loadAll();
  };

  const deleteProgram = async (id) => {
    if (!window.confirm("Are you sure you want to delete this program?"))
      return;
    try {
      await axios.delete(`${BASE_URL}/api/interviews/programs/${id}`, {
        headers: authHeader(),
      });
      await loadAll();
    } catch (e) {
      alert("Failed to delete program.");
    }
  };

  if (loading) return <div>Loading…</div>;

  return (
    <div className="form-wrapper my-2">
      <h2>Interview Questions</h2>
      {err && <div className="alert alert-danger">{err}</div>}

      {/* AVAILABLE QUESTIONS */}
      <fieldset className="border p-3 my-3">
        <legend>Available Questions</legend>
        <button className="btn btn-primary mb-2" onClick={openNewQ}>
          + Add Question
        </button>
        <div className="list-group">
          <div className="input-group mb-3 nw-filter-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPageQuestions(1); // reset to page 1 when searching
              }}
            />
          </div>

          <div className="mb-2">
            {getPaginatedData(
              allQuestions.filter((q) =>
                q.iq_question.toLowerCase().includes(searchQuery.toLowerCase())
              ),
              currentPageQuestions
            ).map((q) => (
              <div
                className="list-group-item d-flex justify-content-between align-items-center"
                key={q.iq_id}
              >
                {q.iq_question}
                <div>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => openEditQ(q)}
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteQuestion(q.iq_id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination
            currentPage={currentPageQuestions}
            totalItems={
              allQuestions.filter((q) =>
                q.iq_question.toLowerCase().includes(searchQuery.toLowerCase())
              ).length
            }
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPageQuestions}
          />
        </div>
      </fieldset>

      {/* DEFAULT PROGRAM */}
      {defaultProgram && (
        <fieldset className="border p-3 mb-3">
          <legend>Default Questions</legend>
          <Select
            options={allQuestions
              .filter(
                (q) =>
                  !safeParse(defaultProgram.ip_question_ids, []).includes(
                    q.iq_id
                  )
              )
              .map((q) => ({ label: q.iq_question, value: q.iq_id }))}
            onChange={(opt) => addQtoProgram(defaultProgram, opt.value)}
            placeholder="Add question..."
          />
          <div className="list-group mt-2">
            {getPaginatedData(
              safeParse(defaultProgram.ip_question_ids, []),
              currentPageDefault
            ).map((id) => {
              const q = allQuestions.find((x) => x.iq_id === id);
              if (!q) return null;
              return (
                <div
                  className="list-group-item d-flex justify-content-between align-items-center"
                  key={id}
                >
                  {q.iq_question}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteQfromProgram(defaultProgram, id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Centered Pagination + Save button */}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <button
              className="btn btn-success"
              onClick={() => saveProgram(defaultProgram)}
            >
              Save
            </button>

            <div className="flex-grow-1 d-flex justify-content-center custom-pagination">
              <Pagination
                currentPage={currentPageDefault}
                totalItems={
                  safeParse(defaultProgram.ip_question_ids, []).length
                }
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPageDefault}
              />
            </div>

            {/* No Delete button for Default, leave empty space for symmetry */}
            <div style={{ width: "75px" }}></div>
          </div>
        </fieldset>
      )}

      {/* PROGRAMS BY CATEGORY */}
      <fieldset className="border p-3">
        <legend>Programs by Category</legend>
        <button
          className="btn btn-outline-primary mb-2"
          onClick={() => setShowProgModal(true)}
        >
          + Add Program
        </button>
        {categoryPrograms.map((p) => (
          <div className="mb-3 border rounded p-2" key={p.ip_id}>
            <h5>{p.ip_name}</h5>

            <div className="mb-2">
              <Select
                isMulti
                options={categories}
                value={safeParse(p.ip_category_ids, []).map((id) =>
                  categories.find((c) => c.value === id)
                )}
                onChange={(opts) => {
                  const ids = (opts || []).map((o) => o.value);
                  p.ip_category_ids = JSON.stringify(ids);
                  setPrograms([...programs]);
                }}
              />
            </div>

            <Select
              options={allQuestions
                .filter(
                  (q) => !safeParse(p.ip_question_ids, []).includes(q.iq_id)
                )
                .map((q) => ({ label: q.iq_question, value: q.iq_id }))}
              onChange={(opt) => addQtoProgram(p, opt.value)}
              placeholder="Add question..."
            />
            <div className="list-group mt-2">
              {getPaginatedData(
                safeParse(p.ip_question_ids, []),
                programPageMap[p.ip_id] || 1
              ).map((id) => {
                const q = allQuestions.find((x) => x.iq_id === id);
                if (!q) return null;
                return (
                  <div
                    className="list-group-item d-flex justify-content-between align-items-center"
                    key={id}
                  >
                    {q.iq_question}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteQfromProgram(p, id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="d-flex justify-content-between align-items-center mt-2">
              <button
                className="btn btn-success"
                onClick={() => saveProgram(p)}
              >
                Save
              </button>

              {/* Centered Pagination */}
              <div className="flex-grow-1 d-flex justify-content-center custom-pagination">
                <Pagination
                  currentPage={programPageMap[p.ip_id] || 1}
                  totalItems={safeParse(p.ip_question_ids, []).length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={(page) =>
                    setProgramPageMap((prev) => ({ ...prev, [p.ip_id]: page }))
                  }
                />
              </div>

              <button
                className="btn btn-danger"
                onClick={() => deleteProgram(p.ip_id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </fieldset>

      {/* QUESTION MODAL */}
      <QuestionEditor
        show={showQModal}
        onClose={() => setShowQModal(false)}
        editingQ={editingQ}
        onSave={saveQuestion}
      />

      {/* PROGRAM MODAL */}
      <Modal show={showProgModal} onHide={() => setShowProgModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>New Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label">Program Name</label>
          <input
            className="form-control mb-2"
            value={progName}
            onChange={(e) => setProgName(e.target.value)}
          />
          <label className="form-label">Categories</label>
          <Select
            isMulti
            options={categories}
            value={progCats}
            onChange={(opts) => setProgCats(opts || [])}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProgModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={createProgram}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ---- QUESTION EDITOR MODAL ----
function QuestionEditor({ show, onClose, onSave, editingQ }) {
  const [type, setType] = useState(editingQ?.iq_type || "text");
  const [text, setText] = useState(editingQ?.iq_question || "");
  const [options, setOptions] = useState(() => {
    const parsed = editingQ?.iq_options
      ? safeParse(editingQ.iq_options, [])
      : [];
    return parsed;
  });

  useEffect(() => {
    if (editingQ) {
      setType(editingQ.iq_type);
      setText(editingQ.iq_question);
      setOptions(safeParse(editingQ.iq_options, []));
    } else {
      setType("text");
      setText("");
      setOptions([]);
    }
  }, [editingQ, show]);

  const addOption = () => setOptions([...options, ""]);
  const updateOption = (i, val) => {
    const copy = [...options];
    copy[i] = val;
    setOptions(copy);
  };
  const deleteOption = (i) => setOptions(options.filter((_, idx) => idx !== i));

  const handleSave = () => {
    onSave({
      iq_question: text,
      iq_type: type,
      iq_options: type === "select" || type === "multiselect" ? options : [],
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingQ ? "Edit Interview Question" : "New Interview Question"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">What is the type of question?</label>
          <select
            className="form-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="text">Text</option>
            <option value="textarea">Paragraph</option>
            <option value="number">Number</option>
            <option value="select">Multiple Choice (Single Selection)</option>
            <option value="multiselect">
              Multiple Choice (Multiple Selections)
            </option>
            <option value="boolean">Yes / No</option>
            <option value="date">Date</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Question</label>
          <textarea
            className="form-control"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {(type === "select" || type === "multiselect") && (
          <div className="mb-3">
            <label className="form-label">Answers</label>
            <div className="list-group">
              {options.map((opt, i) => (
                <div
                  key={i}
                  className="list-group-item d-flex align-items-center gap-2"
                >
                  <input
                    type="text"
                    className="form-control"
                    value={opt}
                    onChange={(e) => updateOption(i, e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteOption(i)}
                  >
                    ❌
                  </button>
                </div>
              ))}
            </div>
            <button
              className="btn btn-outline-secondary btn-sm mt-2"
              onClick={addOption}
            >
              + Add Answer
            </button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
